// Account.js - VERSION TRADUITE COMPLÈTE
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useConversations } from '../context/ConversationContext';
import { 
  User, Lock, ArrowLeft, Edit, Save, Eye, EyeOff, AlertCircle, 
  Check, X, Mail, Calendar, Shield, Key, Crown, Receipt, Download,
  CreditCard, MessageCircle, Brain, BarChart3, Zap
} from 'lucide-react';
import styles from './Account.module.css';
import { API_BASE_URL } from '../config';

const Account = () => {
  const { t, i18n } = useTranslation(['account', 'common', 'errors']);
  const navigate = useNavigate();
  const { user, loading, updateUserInfo, refreshUserProfile } = useConversations();
  
  // Main states
  const [activeTab, setActiveTab] = useState('abonnement');
  const [editMode, setEditMode] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  // Password visibility states
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // User info editing states
  const [userInfo, setUserInfo] = useState({
    username: '',
    email: '',
    pseudo: '',
    firstName: '',
    lastName: ''
  });

  // Password change states
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [passwordStrength, setPasswordStrength] = useState(0);

  // Initialize user data
  useEffect(() => {
    if (user) {
      setUserInfo({
        username: user.username || '',
        email: user.email || '',
        pseudo: user.pseudo || user.username || '',
        firstName: user.firstName || '',
        lastName: user.lastName || ''
      });
    }
  }, [user]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('payment_success') || urlParams.get('session_id')) {
      refreshUserProfile();
    }
  }, [refreshUserProfile]);

  // Auto-hide messages after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ type: '', text: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Calculate password strength
  const calculatePasswordStrength = useCallback((password) => {
    let strength = 0;
    let feedback = [];

    if (password.length >= 8) {
      strength += 25;
    } else {
      feedback.push(t('account:password.feedback.min_8'));
    }

    if (/[a-z]/.test(password)) {
      strength += 25;
    } else {
      feedback.push(t('account:password.feedback.lowercase'));
    }

    if (/[A-Z]/.test(password)) {
      strength += 25;
    } else {
      feedback.push(t('account:password.feedback.uppercase'));
    }

    if (/[0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
      strength += 25;
    } else {
      feedback.push(t('account:password.feedback.number_symbol'));
    }

    setPasswordStrength(strength);
    return { strength, feedback };
  }, [t]);

  // Handle password change
  const handlePasswordChange = useCallback((field, value) => {
    setPasswordData(prev => ({ ...prev, [field]: value }));
    if (field === 'newPassword') {
      calculatePasswordStrength(value);
    }
  }, [calculatePasswordStrength]);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback((field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  }, []);

  // Save user info
  const handleSaveInfo = async () => {
    if (!userInfo.username.trim() || !userInfo.email.trim()) {
      setMessage({ 
        type: 'error', 
        text: t('account:messages.username_email_required')
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userInfo.email)) {
      setMessage({ 
        type: 'error', 
        text: t('account:messages.invalid_email')
      });
      return;
    }

    setUpdating(true);
    try {
      await updateUserInfo(userInfo);
      setMessage({ 
        type: 'success', 
        text: t('account:messages.info_updated')
      });
      setEditMode(false);
    } catch (error) {
      console.error('Update error:', error);
      setMessage({ 
        type: 'error', 
        text: error.message || t('account:messages.update_error')
      });
    } finally {
      setUpdating(false);
    }
  };

  // Change password
  const handleChangePassword = async () => {
    if (!passwordData.currentPassword.trim()) {
      setMessage({ 
        type: 'error', 
        text: t('account:messages.current_password_required')
      });
      return;
    }

    if (!passwordData.newPassword.trim()) {
      setMessage({ 
        type: 'error', 
        text: t('account:messages.new_password_required')
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ 
        type: 'error', 
        text: t('account:messages.passwords_mismatch')
      });
      return;
    }

    if (passwordStrength < 75) {
      setMessage({ 
        type: 'error', 
        text: t('account:password.not_strong_enough')
      });
      return;
    }

    setUpdating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
          confirmPassword: passwordData.confirmPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: t('account:messages.password_changed')
        });
        setPasswordData({ 
          currentPassword: '', 
          newPassword: '', 
          confirmPassword: '' 
        });
        setPasswordStrength(0);
      } else {
        if (data.error === 'Invalid current password') {
          setMessage({ 
            type: 'error', 
            text: t('account:messages.incorrect_password')
          });
        } else {
          setMessage({ 
            type: 'error', 
            text: data.message || t('account:messages.password_change_error')
          });
        }
      }
    } catch (error) {
      console.error('Password change error:', error);
      setMessage({ 
        type: 'error', 
        text: t('account:messages.password_change_error')
      });
    } finally {
      setUpdating(false);
    }
  };

  // Suppression de compte
const handleDeleteAccount = async () => {
  if (deleteConfirmText !== 'SUPPRIMER') {
    setMessage({ type: 'error', text: 'Tapez SUPPRIMER pour confirmer' });
    return;
  }

  setDeleting(true);
  try {
    const response = await fetch(`${API_BASE_URL}/auth/account`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    const data = await response.json();

    if (data.success) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login?account_deleted=true');
    } else {
      setMessage({ type: 'error', text: data.message || 'Erreur lors de la suppression' });
      setShowDeleteModal(false);
    }
  } catch (error) {
    console.error('Delete account error:', error);
    setMessage({ type: 'error', text: 'Erreur serveur, réessayez' });
    setShowDeleteModal(false);
  } finally {
    setDeleting(false);
  }
};

  // Navigate to subscription page
  const handleUpgrade = () => {
    navigate('/subscription');
  };

  // Cancel subscription
  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  const handleReactivateSubscription = async () => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_BASE_URL}/payment/reactivate-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: t('account:messages.subscription_reactivated')
        });
        await refreshUserProfile();
      } else {
        setMessage({ 
          type: 'error', 
          text: data.message || t('errors:generic')
        });
      }
    } catch (error) {
      console.error('Reactivation error:', error);
      setMessage({ 
        type: 'error', 
        text: t('errors:generic')
      });
    } finally {
      setUpdating(false);
    }
  };
const getCurrencySymbol = () => {
    const lastInvoiceCurrency = user?.subscription?.currency || 
                                invoices?.[0]?.currency || 
                                'USD';
    return lastInvoiceCurrency.toUpperCase() === 'EUR' ? '€' : '$';
};
  // Confirm cancellation
  const confirmCancelSubscription = async () => {
    setUpdating(true);
    setShowCancelModal(false);
    
    try {
      const response = await fetch(`${API_BASE_URL}/payment/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setMessage({ 
          type: 'success', 
          text: t('account:messages.subscription_canceled')
        });
        await refreshUserProfile();
      } else {
        setMessage({ 
          type: 'error', 
          text: data.message || t('errors:generic')
        });
      }
    } catch (error) {
      console.error('Cancellation error:', error);
      setMessage({ 
        type: 'error', 
        text: t('errors:generic')
      });
    } finally {
      setUpdating(false);
    }
  };

  // Get subscription info
  const getSubscriptionInfo = () => {
    if (loading || !user) return null;

    const hasActiveSubscription = user.subscription && 
                                   user.subscription.status === 'active';

    if (hasActiveSubscription) {
      const planName = user.subscription.plan_name;
      const isCanceled = user.subscription.canceled_at;
      const expiresAt = new Date(user.subscription.expires_at);
      const now = new Date();
      const willExpireSoon = expiresAt - now < 7 * 24 * 60 * 60 * 1000;
      const sym = invoices?.[0]?.currency === 'EUR' ? '€' : '$';
      const isEur = invoices?.[0]?.currency === 'EUR';
      if (planName === 'plan1') {
        return {
          label: t('account:subscription.starter_plan'),
          color: '#10b981',
          icon: '⭐',
          price: `${isEur ? '5.99' : '5.99'}${sym}`,
          features: [
            t('account:subscription.features.ai_responses'),
            t('account:subscription.features.analysis'),
            t('account:subscription.features.reformulation')
          ],
          isCanceled,
          willExpireSoon,
          expiresAt
        };
      } else if (planName === 'plan2') {
        return {
          label: t('account:subscription.premium_plan'),
          color: '#2563eb',
          icon: '💎',
          price: `${isEur ? '10.99' : '11.99'}${sym}`,
          features: [
            t('account:subscription.features.ai_responses'),
            t('account:subscription.features.analysis'),
            t('account:subscription.features.reformulation'),
            t('account:subscription.features.unlimited_conversations')
          ],
          isCanceled,
          willExpireSoon,
          expiresAt
        };
      } else if (planName === 'plan3') {
        return {
          label: t('account:subscription.pro_plan'),
          color: '#f59e0b',
          icon: '👑',
          price: `${isEur ? '21.99' : '23.99'}${sym}`,
          features: [
            t('account:subscription.features.ai_responses'),
            t('account:subscription.features.analysis'),
            t('account:subscription.features.reformulation'),
            t('account:subscription.features.unlimited_conversations'),
            t('account:subscription.features.priority_support')
          ],
          isCanceled,
          willExpireSoon,
          expiresAt
        };
      }
    }

    return {
      label: t('account:subscription.free_plan'),
      color: '#6b7280',
      icon: '🆓',
      features: []
    };
  };

  // Get usage stats
  const getUsageStats = () => {
    if (!user) return { conversations: 0, messagesCreated: 0, sentimentAnalyses: 0, reformulations: 0 };
    
    return {
      conversations: user.stats?.total_conversations || 0,
      messagesCreated: user.stats?.messages_created || 0,
      sentimentAnalyses: user.stats?.sentiment_analyses || 0,
      reformulations: user.stats?.reformulations || 0
    };
  };

  // Get password strength info
  const getPasswordStrengthInfo = () => {
    if (passwordStrength < 25) return { label: t('account:password.strength.weak'), color: '#ef4444' };
    if (passwordStrength < 50) return { label: t('account:password.strength.medium'), color: '#f59e0b' };
    if (passwordStrength < 75) return { label: t('account:password.strength.good'), color: '#3b82f6' };
    return { label: t('account:password.strength.strong'), color: '#10b981' };
  };

 const fetchInvoices = useCallback(async () => {
  if (!user) {
    console.log('No user, skipping invoice fetch');
    return;
  }

  const token = localStorage.getItem('token');
  if (!token) {
    console.log('No token found');
    return;
  }

  setLoadingInvoices(true);

  try {
    const response = await fetch(`${API_BASE_URL}/invoices`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        // pas obligatoire pour un GET, mais possible :
        'Content-Type': 'application/json'
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('Invoices received:', data);

      // 👉 Adapter au backend
      if (data.success && Array.isArray(data.invoices)) {
        setInvoices(data.invoices);
      } else {
        console.log('No invoices in response');
        setInvoices([]);
      }
    } else {
      const errorData = await response.text();
      console.error('Invoice fetch failed:', response.status, errorData);
      setInvoices([]);
    }
  } catch (error) {
    console.error('Invoice fetch error:', error);
    setInvoices([]);
  } finally {
    setLoadingInvoices(false);
  }
}, [user]);

  const downloadInvoice = async (invoiceId, invoiceNumber) => {
    try {
      const token = localStorage.getItem('token');

     const response = await fetch(`${API_BASE_URL}/invoices/${invoiceId}/pdf`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });


      if (response.ok) {
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/pdf')) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `facture-${invoiceNumber}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          setMessage({ 
            type: 'success', 
            text: `Facture ${invoiceNumber} téléchargée avec succès`
          });
        } else {
          const data = await response.json();
          if (data.invoice_data) {
            generateClientSidePDF(data.invoice_data, invoiceNumber);
          } else {
            setMessage({ 
              type: 'error', 
              text: 'PDF non disponible' 
            });
          }
        }
      } else {
        const errorData = await response.text();
        console.error('PDF download error:', errorData);
        setMessage({ 
          type: 'error', 
          text: 'Erreur lors du téléchargement du PDF' 
        });
      }
    } catch (error) {
      console.error('Download error:', error);
      setMessage({ 
        type: 'error', 
        text: 'Erreur lors du téléchargement' 
      });
    }
  };

  const generateClientSidePDF = (invoiceData, invoiceNumber) => {
    try {
      const jsPDF = window.jsPDF;
      if (!jsPDF) {
        setMessage({ 
          type: 'error', 
          text: 'jsPDF not loaded' 
        });
        return;
      }

      const doc = new jsPDF();
      doc.text(`Facture #${invoiceNumber}`, 20, 20);
      doc.text(`Date: ${invoiceData.invoice_date}`, 20, 30);
      doc.text(`Total: ${invoiceData.total_ttc}${invoiceData.currency === 'EUR' ? '€' : '$'}`, 20, 40);
      doc.save(`facture-${invoiceNumber}.pdf`);
    } catch (error) {
      console.error('PDF generation error:', error);
      setMessage({ 
        type: 'error', 
        text: 'Erreur lors de la génération du PDF' 
      });
    }
  };

  useEffect(() => {
    if (activeTab === 'facturation' && user) {
      fetchInvoices();
    }
  }, [activeTab, user, fetchInvoices]);

  const passwordsMatch = passwordData.newPassword && 
                        passwordData.confirmPassword && 
                        passwordData.newPassword === passwordData.confirmPassword;

  const subscriptionInfo = getSubscriptionInfo();
  const usageStats = getUsageStats();
  const passwordStrengthInfo = getPasswordStrengthInfo();

  if (loading) {
    return (
      <div className={styles["account-page"]}>
        <div className={styles["account-loading"]}>
          <div className={styles["loading-spinner"]}></div>
          <p className={styles["loading-text"]}>{t('account:loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles["account-page"]}>
      <div className={styles["account-container"]}>
        {/* Header */}
        <div className={styles["account-header"]}>
          <button 
            className={styles["back-button"]}
            onClick={() => navigate('/dashboard')}
          >
            <ArrowLeft size={20} />
            {t('account:back_dashboard')}
          </button>
          
          <h1 className={styles["page-title"]}>{t('account:title')}</h1>
        </div>

        {/* Tabs Navigation */}
        <div className={styles["account-tabs"]}>
          <button
            className={`${styles["tab"]} ${activeTab === 'abonnement' ? styles["active"] : ''}`}
            onClick={() => setActiveTab('abonnement')}
          >
            <Crown size={18} />
            {t('account:tabs.subscription')}
          </button>
          <button
            className={`${styles["tab"]} ${activeTab === 'profil' ? styles["active"] : ''}`}
            onClick={() => setActiveTab('profil')}
          >
            <User size={18} />
            {t('account:tabs.profile')}
          </button>
          <button
            className={`${styles["tab"]} ${activeTab === 'securite' ? styles["active"] : ''}`}
            onClick={() => setActiveTab('securite')}
          >
            <Shield size={18} />
            {t('account:tabs.security')}
          </button>
          <button
            className={`${styles["tab"]} ${activeTab === 'facturation' ? styles["active"] : ''}`}
            onClick={() => setActiveTab('facturation')}
          >
            <Receipt size={18} />
            {t('account:tabs.billing')}
          </button>
          <button
            className={`${styles["tab"]} ${activeTab === 'stats' ? styles["active"] : ''}`}
            onClick={() => setActiveTab('stats')}
          >
            <BarChart3 size={18} />
            {t('account:tabs.stats')}
          </button>
          <button
              className={`${styles["tab"]} ${activeTab === 'danger' ? styles["active"] : ''}`}
              onClick={() => setActiveTab('danger')}
              style={{ color: activeTab === 'danger' ? '#ef4444' : undefined }}
            >
              <X size={18} />
              Compte
          </button>
        </div>

        {/* Message display */}
        {message.text && (
          <div className={`${styles["message"]} ${message.type}`}>
            {message.type === 'success' ? <Check size={16} /> : <AlertCircle size={16} />}
            {message.text}
          </div>
        )}

        {/* Content wrapper */}
        <div className={styles["account-content"]}>

        {/* Subscription Tab */}
        {activeTab === 'abonnement' && subscriptionInfo && (
          <div className={styles["tab-content"]}>
            <div className={styles["subscription-section"]}>
              <h2>
                <Crown className={styles["section-icon"]} />
                {t('account:subscription.title')}
              </h2>

              <div className={styles["subscription-card"]} style={{ borderColor: subscriptionInfo.color }}>
                <div className={styles["subscription-header"]}>
                  <div className={styles["subscription-badge"]} style={{ backgroundColor: subscriptionInfo.color }}>
                    <span className={styles["badge-icon"]}>{subscriptionInfo.icon}</span>
                    <span className={styles["badge-label"]}>{subscriptionInfo.label}</span>
                  </div>
                  
                  {subscriptionInfo.isCanceled && (
                    <div className={`${styles["status-badge"]} ${styles["canceled"]}`}>
                      {t('account:subscription.canceled')}
                    </div>
                  )}
                  {!subscriptionInfo.isCanceled && user?.subscription && (
                    <div className={`${styles["status-badge"]} ${styles["active"]}`}>
                      {t('account:subscription.active')}
                    </div>
                  )}
                </div>

                {user?.subscription && (
                  <div className={styles["subscription-info"]}>
                    <div className={styles["info-item"]}>
                      <Calendar size={16} />
                      <span>{t('account:subscription.expires_on')}: {new Date(subscriptionInfo.expiresAt).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                    </div>
                    
                    {subscriptionInfo.willExpireSoon && (
                      <div className={styles["expiry-warning"]}>
                        <AlertCircle size={16} />
                        {t('account:subscription.expiring_soon')}
                      </div>
                    )}
                  </div>
                )}

                {subscriptionInfo.price && (
                  <div className={styles["subscription-price"]}>
                    <span className={styles["price"]}>{subscriptionInfo.price}</span>
                    <span className={styles["period"]}>{t('account:subscription.monthly')}</span>
                  </div>
                )}

                {subscriptionInfo.features.length > 0 && (
                  <div className={styles["subscription-features"]}>
                    <h4>{t('account:subscription.includes')}</h4>
                    <ul>
                      {subscriptionInfo.features.map((feature, index) => (
                        <li key={index}>
                          <Check size={16} />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {user?.subscription && (
                  <div className={styles["subscription-stats"]}>
                    <div className={styles["stat"]}>
                      <Zap size={18} />
                      <div>
                        <strong>{user.remaining_credits || 0}</strong>
                        <span>{t('account:subscription.credits_remaining', { count: user.remaining_credits || 0 })}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className={styles["subscription-actions"]}>
                  {!user?.subscription && (
                    <button 
                      className={styles["upgrade-button"]}
                      onClick={handleUpgrade}
                    >
                      <Crown size={16} />
                      {t('account:subscription.upgrade_button')} Premium
                    </button>
                  )}

                  {user?.subscription && !subscriptionInfo.isCanceled && (
                    <>
                      <button 
                        className={styles["manage-button"]}
                        onClick={handleUpgrade}
                      >
                        {t('account:subscription.manage_button')}
                      </button>
                      <button 
                        className={styles["cancel-button"]}
                        onClick={handleCancelSubscription}
                      >
                        {t('account:subscription.cancel_button')}
                      </button>
                    </>
                  )}

                  {user?.subscription && subscriptionInfo.isCanceled && (
                    <button 
                      className={styles["reactivate-button"]}
                      onClick={handleReactivateSubscription}
                      disabled={updating}
                    >
                      {t('account:subscription.reactivate_button')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === 'profil' && (
          <div className={styles["tab-content"]}>
            <div className={styles["profile-section"]}>
              <div className={styles["section-header"]}>
                <h2>
                  <User className={styles["section-icon"]} />
                  {t('account:profile.title')}
                </h2>
                {!editMode && (
                  <button 
                    className={styles["edit-button"]}
                    onClick={() => setEditMode(true)}
                  >
                    <Edit size={16} />
                    {t('account:profile.edit_button')}
                  </button>
                )}
              </div>

              <div className={styles["profile-form"]}>
                <div className={styles["form-group"]}>
                  <label>
                    <Mail size={16} />
                    {t('account:profile.username_label')}
                  </label>
                  <input
                    type="text"
                    value={userInfo.username}
                    onChange={(e) => setUserInfo({...userInfo, username: e.target.value})}
                    disabled={!editMode}
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label>
                    <Mail size={16} />
                    {t('account:profile.email_label')}
                  </label>
                  <input
                    type="email"
                    value={userInfo.email}
                    onChange={(e) => setUserInfo({...userInfo, email: e.target.value})}
                    disabled={!editMode}
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label>
                    <User size={16} />
                    {t('account:profile.pseudo_label')}
                  </label>
                  <input
                    type="text"
                    value={userInfo.pseudo}
                    onChange={(e) => setUserInfo({...userInfo, pseudo: e.target.value})}
                    disabled={!editMode}
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label>
                    <User size={16} />
                    {t('account:profile.firstname_label')}
                  </label>
                  <input
                    type="text"
                    value={userInfo.firstName}
                    onChange={(e) => setUserInfo({...userInfo, firstName: e.target.value})}
                    disabled={!editMode}
                  />
                </div>

                <div className={styles["form-group"]}>
                  <label>
                    <User size={16} />
                    {t('account:profile.lastname_label')}
                  </label>
                  <input
                    type="text"
                    value={userInfo.lastName}
                    onChange={(e) => setUserInfo({...userInfo, lastName: e.target.value})}
                    disabled={!editMode}
                  />
                </div>

                {editMode && (
                  <div className={styles["form-actions"]}>
                    <button 
                      className={styles["save-button"]}
                      onClick={handleSaveInfo}
                      disabled={updating}
                    >
                      <Save size={16} />
                      {updating ? t('common:common.loading') : t('account:profile.save_button')}
                    </button>
                    <button 
                      className={styles["cancel-button"]}
                      onClick={() => setEditMode(false)}
                    >
                      {t('account:profile.cancel_button')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'securite' && (
          <div className={styles["tab-content"]}>
            <div className={styles["security-section"]}>
              <h2>
                <Shield className={styles["section-icon"]} />
                {t('account:security.title')}
              </h2>

              <div className={styles["password-change-form"]}>
                <h3>
                  <Key size={18} />
                  {t('account:security.change_password_title')}
                </h3>

                <div className={styles["form-group"]}>
                  <label>{t('account:security.current_password_label')}</label>
                  <div className={styles["password-input-container"]}>
                    <input
                      type={showPasswords.current ? 'text' : 'password'}
                      value={passwordData.currentPassword}
                      onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('current')}
                      aria-label="Toggle password visibility"
                    >
                      {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className={styles["form-group"]}>
                  <label>{t('account:security.new_password_label')}</label>
                  <div className={styles["password-input-container"]}>
                    <input
                      type={showPasswords.new ? 'text' : 'password'}
                      value={passwordData.newPassword}
                      onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      aria-label="Toggle password visibility"
                    >
                      {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  
                  {passwordData.newPassword && (
                    <div className={styles["password-strength"]}>
                      <div className={styles["strength-bar"]}>
                        <div 
                          className={styles["strength-fill"]} 
                          style={{ 
                            width: `${passwordStrength}%`,
                            backgroundColor: passwordStrengthInfo.color
                          }}
                        />
                      </div>
                      <span style={{ color: passwordStrengthInfo.color }}>
                        {passwordStrengthInfo.label}
                      </span>
                    </div>
                  )}
                </div>

                <div className={styles["form-group"]}>
                  <label>{t('account:security.confirm_password_label')}</label>
                  <div className={styles["password-input-container"]}>
                    <input
                      type={showPasswords.confirm  ? styles['text'] : 'password'}
                      value={passwordData.confirmPassword}
                      onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      aria-label="Toggle password visibility"
                    >
                      {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  
                  {passwordData.confirmPassword && (
                    <div style={{ 
                      marginTop: '0.5rem', 
                      fontSize: '0.875rem',
                      color: passwordsMatch ? '#10b981' : '#ef4444',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      {passwordsMatch ? <Check size={14} /> : <X size={14} />}
                      {passwordsMatch ? t('account:security.passwords_match') : t('account:security.passwords_dont_match')}
                    </div>
                  )}
                </div>

                <button 
                  className={styles["change-password-button"]}
                  onClick={handleChangePassword}
                  disabled={updating || passwordStrength < 75 || !passwordsMatch}
                >
                  <Lock size={16} />
                  {updating ? t('account:security.changing') : t('account:security.change_button')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Billing Tab */}
        {activeTab === 'facturation' && (
          <div className={styles["tab-content"]}>
            <div className={styles["billing-section"]}>
              <div className={styles["section-header"]}>
                <h2>
                  <Receipt className={styles["section-icon"]} />
                  {t('account:billing.title')}
                </h2>
                <button 
                  onClick={() => {
                    console.log('Debug invoices:', {
                      user: !!user,
                      activeTab,
                      loadingInvoices,
                      invoicesLength: invoices.length,
                      invoices
                    });
                    fetchInvoices();
                  }}
                  style={{ 
                    padding: '0.5rem', 
                    background: '#3b82f6', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem'
                  }}
                >
                  🔄 {t('account:billing.reload_button')}
                </button>
              </div>
              
              <div className={styles["billing-info"]}>
                <div className={styles["billing-card"]}>
                  <div className={styles["card-header"]}>
                    <CreditCard size={20} />
                    <h3>{t('account:billing.payment_method')}</h3>
                  </div>
                  <p>{t('account:billing.secure_payments')}</p>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    {t('account:billing.cards_accepted')}
                  </div>
                </div>

                <div className={styles["invoice-history"]}>
                  <h3>{t('account:billing.recent_invoices')}</h3>
                  
                  {loadingInvoices ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <div className={styles["loading-spinner"]}></div>
                      <p>{t('account:billing.loading_invoices')}</p>
                    </div>
                  ) : invoices.length > 0 ? (
                    <div className={styles["invoices-list"]}>
                      {invoices.map((invoice) => (
                        <div key={invoice.id} className={styles["invoice-item"]}>
                          <div className={styles["invoice-header"]}>
                            <div>
                              <h4>{t('account:billing.invoice_number', { number: invoice.invoice_number })}</h4>
                              <p>{t('account:billing.plan', { name: invoice.plan_name })}</p>
                            </div>
                            <div className={styles["invoice-amount"]}>
                              {invoice.total_ttc}{invoice.currency === 'EUR' ? '€' : '$'}
                            </div>
                          </div>
                          
                          <div className={styles["invoice-details"]}>
                            <div className={styles["invoice-meta"]}>
                              <span>{t('account:billing.date')} {new Date(invoice.invoice_date).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                              <span className={`${styles["status"]} ${invoice.payment_status}`}>
                                {invoice.payment_status === 'paid' ? t('account:billing.status_paid') : 
                                 invoice.payment_status === 'pending' ? t('account:billing.status_pending') : 
                                 t('account:billing.status_unpaid')}
                              </span>
                            </div>
                            
                            {invoice.lines && invoice.lines.length > 0 && (
                              <div className={styles["invoice-lines"]}>
                                {invoice.lines.map((line, index) => (
                                  <div key={index} className={styles["invoice-line"]}>
                                    <span>{line.product_name}</span>
                                    <span>{line.description}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            <div className={styles["invoice-actions"]}>
                              <button 
                                className={styles["download-button"]}
                                onClick={() => downloadInvoice(invoice.id, invoice.invoice_number)}
                              >
                                <Download size={16} />
                                {t('account:billing.download_button')}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles["empty-state"]}>
                      <Receipt size={48} />
                      <p>{t('account:billing.no_invoices')}</p>
                      <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                        {t('account:billing.no_invoices_desc')}
                      </p>
                      <div style={{ 
                        marginTop: '1rem', 
                        padding: '0.5rem', 
                        background: '#f3f4f6', 
                        borderRadius: '0.25rem',
                        fontSize: '0.75rem',
                        color: '#6b7280'
                      }}>
                        Debug: user={user ? 'OK' : 'NON'}, loading={loadingInvoices ? 'OUI' : 'NON'}, count={invoices.length}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className={styles["tab-content"]}>
            <div className={styles["stats-section"]}>
              <h2>
                <BarChart3 className={styles["section-icon"]} />
                {t('account:stats.title')}
              </h2>

              <div className={styles["stats-grid"]}>
                <div className={styles["stat-card"]}>
                  <div className={styles["stat-header"]}>
                    <h3>{t('account:stats.usage')}</h3>
                  </div>
                  <div className={styles["stats-list"]}>
                    <div className={styles["stat-item"]}>
                      <MessageCircle size={18} />
                      <div>
                        <span className={styles["stat-label"]}>{t('account:stats.conversations_count')}</span>
                        <span className={styles["stat-value"]}>{usageStats.conversations}</span>
                      </div>
                    </div>
                    <div className={styles["stat-item"]}>
                      <CreditCard size={18} />
                      <div>
                        <span className={styles["stat-label"]}>{t('account:stats.credits_used')}</span>
                        <span className={styles["stat-value"]}>{user?.used_credits || 0} / {user?.total_credits || 0}</span>
                      </div>
                    </div>
                    <div className={styles["stat-item"]}>
                      <MessageCircle size={18} />
                      <div>
                        <span className={styles["stat-label"]}>{t('account:stats.messages_sent')}</span>
                        <span className={styles["stat-value"]}>{usageStats.messagesCreated}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {user?.subscription && (
                  <div className={styles["stat-card"]}>
                    <div className={styles["stat-header"]}>
                      <Crown size={18} />
                      <h3>{t('account:stats.premium_features')}</h3>
                    </div>
                    <div className={styles["stats-list"]}>
                      <div className={styles["stat-item"]}>
                        <Brain size={18} />
                        <div>
                          <span className={styles["stat-label"]}>{t('account:stats.analyses_count')}</span>
                          <span className={styles["stat-value"]}>{usageStats.sentimentAnalyses}</span>
                        </div>
                      </div>
                      <div className={styles["stat-item"]}>
                        <MessageCircle size={18} />
                        <div>
                          <span className={styles["stat-label"]}>{t('account:stats.reformulations_count')}</span>
                          <span className={styles["stat-value"]}>{usageStats.reformulations}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className={styles["stat-card"]}>
                  <div className={styles["stat-header"]}>
                    <Calendar size={18} />
                    <h3>{t('account:stats.member_since')}</h3>
                  </div>
                  <div className={styles["stats-list"]}>
                    <div className={styles["stat-item"]}>
                      <Calendar size={18} />
                      <div>
                        <span className={styles["stat-label"]}>{t('account:stats.member_since')}</span>
                        <span className={styles["stat-value"]}>
                          {user?.created_at ? new Date(user.created_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US') : '-'}
                        </span>
                      </div>
                    </div>
                    <div className={styles["stat-item"]}>
                      <Calendar size={18} />
                      <div>
                        <span className={styles["stat-label"]}>{t('account:stats.last_activity')}</span>
                        <span className={styles["stat-value"]}>
                          {user?.last_login ? new Date(user.last_login).toLocaleDateString(i18n.language === 'fr' ? 'fr-FR' : 'en-US') : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        </div>
        {/* End account-content */}

        {/* Danger Zone Tab */}
{activeTab === 'danger' && (
  <div className={styles["tab-content"]}>
    <div className={styles["subscription-section"]}>
      <h2 style={{ color: '#ef4444' }}>
        <AlertCircle size={20} style={{ marginRight: 8 }} />
        Zone de danger
      </h2>

      <div style={{
        border: '1px solid rgba(239,68,68,0.3)',
        borderRadius: 12,
        padding: 24,
        backgroundColor: 'rgba(239,68,68,0.05)'
      }}>
        <h3 style={{ color: '#f87171', marginBottom: 8 }}>Supprimer mon compte</h3>
        <p style={{ color: '#94a3b8', marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
          Cette action est <strong style={{ color: '#fca5a5' }}>irréversible</strong>. 
          Votre compte, vos conversations et vos données seront supprimés définitivement. 
          Votre abonnement actif sera annulé immédiatement sans remboursement.
        </p>
        <button
          onClick={() => setShowDeleteModal(true)}
          style={{
            backgroundColor: 'rgba(239,68,68,0.15)',
            border: '1px solid rgba(239,68,68,0.4)',
            color: '#f87171',
            padding: '10px 20px',
            borderRadius: 8,
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 14
          }}
        >
          Supprimer mon compte
        </button>
      </div>
    </div>
  </div>
)}

        {/* Cancel Subscription Modal */}
        {showCancelModal && (
          <div className={styles["modal-overlay"]}>
            <div className={styles["modal-content"]}>
              <div className={styles["modal-header"]}>
                <div className={styles["modal-icon"]}>
                  <AlertCircle size={24} />
                </div>
                <h3 className={styles["modal-title"]}>{t('account:modal.cancel_subscription_title')}</h3>
              </div>
              
              <div className={styles["modal-body"]}>
                <p>{t('account:modal.cancel_confirmation', { plan: subscriptionInfo.label })}</p>
                
                <div className={styles["modal-info"]}>
                  <div className={styles["info-item"]}>
                    <Check size={16} />
                    <span>{t('account:modal.keep_access')}</span>
                  </div>
                  <div className={styles["info-item"]}>
                    <Check size={16} />
                    <span>{t('account:modal.keep_credits')}</span>
                  </div>
                  <div className={styles["info-item"]}>
                    <Check size={16} />
                    <span>{t('account:modal.resubscribe_anytime')}</span>
                  </div>
                </div>
              </div>
              
              <div className={styles["modal-actions"]}>
                <button 
                  className={styles["modal-cancel"]}
                  onClick={() => setShowCancelModal(false)}
                >
                  {t('account:modal.keep_subscription')}
                </button>
                <button 
                  className={styles["modal-confirm"]}
                  onClick={confirmCancelSubscription}
                  disabled={updating}
                >
                  <X size={16} />
                  {updating ? t('account:modal.canceling') : t('account:modal.confirm_cancel')}
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Delete Account Modal */}
{showDeleteModal && (
  <div className={styles["modal-overlay"]}>
    <div className={styles["modal-content"]}>
      <div className={styles["modal-header"]}>
        <div className={styles["modal-icon"]} style={{ color: '#ef4444' }}>
          <AlertCircle size={24} />
        </div>
        <h3 className={styles["modal-title"]} style={{ color: '#f87171' }}>
          Supprimer définitivement mon compte
        </h3>
      </div>

      <div className={styles["modal-body"]}>
        <p style={{ marginBottom: 16 }}>
          Cette action supprimera définitivement votre compte et toutes vos données. 
          Tapez <strong>SUPPRIMER</strong> pour confirmer.
        </p>
        <input
          type="text"
          value={deleteConfirmText}
          onChange={(e) => setDeleteConfirmText(e.target.value)}
          placeholder="Tapez SUPPRIMER"
          style={{
            width: '100%',
            padding: '10px 14px',
            backgroundColor: 'rgba(15,23,42,0.6)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8,
            color: '#f8fafc',
            fontSize: 14,
            boxSizing: 'border-box'
          }}
        />
      </div>

      <div className={styles["modal-actions"]}>
        <button
          className={styles["modal-cancel"]}
          onClick={() => { setShowDeleteModal(false); setDeleteConfirmText(''); }}
          disabled={deleting}
        >
          Annuler
        </button>
        <button
          className={styles["modal-confirm"]}
          onClick={handleDeleteAccount}
          disabled={deleting || deleteConfirmText !== 'SUPPRIMER'}
          style={{ backgroundColor: '#dc2626' }}
        >
          <X size={16} />
          {deleting ? 'Suppression...' : 'Supprimer définitivement'}
        </button>
      </div>
    </div>
  </div>
)}
      </div>
    </div>
  );
};

export default Account;